const API_URL = process.env.REACT_APP_API_URL;

export const userService = {
  _cachedUser: null,

  // ===== Register User =====
  async registerUser(payload) {
    try {
      const response = await fetch(`${API_URL}/api/users/save-name`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to register user");
      return await response.json();
    } catch (err) {
      console.error("❌ userService.registerUser error:", err.message);
      throw err;
    }
  },

  // ===== Login =====
  async login(email, password) {
    try {
      const response = await fetch(`${API_URL}/api/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json();

      if (!response.ok) {
        // If subscription required, throw proper Error with subscription data
        if (result.subscriptionRequired) {
          const error = new Error(JSON.stringify(result));
          error.subscriptionRequired = true;
          error.data = result;
          throw error;
        }
        throw new Error(result.error || "Invalid credentials");
      }

      const { user, accessToken, refreshToken } = result;
      if (!accessToken) throw new Error("No accessToken from server");

      // Store tokens & cache user
      this._cachedUser = user;
      localStorage.setItem("authUser", JSON.stringify(user));
      localStorage.setItem("authToken", accessToken);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);

      return user;
    } catch (err) {
      console.error("❌ userService.login error:", err.message);
      throw err;
    }
  },

  // ===== Refresh Access Token =====
  async refreshAccessToken() {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      console.warn("No refresh token available");
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/api/users/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to refresh token");

      const { accessToken, refreshToken: newRefreshToken } = result;

      // ✅ Update tokens in storage
      localStorage.setItem("authToken", accessToken);
      if (newRefreshToken)
        localStorage.setItem("refreshToken", newRefreshToken);

      console.log("✅ Access token refreshed successfully");
      return accessToken;
    } catch (err) {
      console.error("❌ Failed to refresh token:", err.message);
      this.logout(); // clear invalid tokens
      return null;
    }
  },

  // ===== Auth Header Helper =====
  _getAuthHeaders() {
    const token = localStorage.getItem("authToken");
    const headers = { "Content-Type": "application/json" };
    if (token && token !== "null" && token !== "undefined") {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  },

  // ===== Get Current User =====
  async getMe() {
    try {
      const response = await fetch(`${API_URL}/api/users/me`, {
        method: "GET",
        headers: this._getAuthHeaders(),
      });

      // If token expired or invalid -> attempt refresh logic (existing)
      if (response.status === 401) {
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          // Retry once with refreshed token
          const retry = await fetch(`${API_URL}/api/users/me`, {
            method: "GET",
            headers: this._getAuthHeaders(),
          });

          if (retry.ok) {
            const retryJson = await retry.json();
            // backend returns { success: true, data: user }
            return retryJson.data;
          }
        }

        // Failed to refresh -> logout
        await this.logout();
        throw new Error("Session expired. Please log in again.");
      }

      const json = await response.json();
      // backend returns { success: true, data: user }
      if (!response.ok) {
        throw new Error(json.message || "Failed to fetch user");
      }

      return json.data; // return raw user object
    } catch (err) {
      console.error("Error fetching current user:", err);
      throw err;
    }
  },

  async getMeWithCache() {
    // Always return { data: user } shape so callers remain consistent
    if (this._cachedUser) {
      return { data: this._cachedUser };
    }

    // getMe() will return the raw user or throw
    const user = await this.getMe();
    this._cachedUser = user;
    return { data: user };
  },

  async isAuthenticated() {
    const me = await this.getMeWithCache();
    return !!me?.data;
  },

  // ===== Logout =====
  async logout() {
    this._cachedUser = null;
    localStorage.removeItem("authUser");
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
  },

  getCurrentUser() {
    if (this._cachedUser) return this._cachedUser;
    const userStr = localStorage.getItem("authUser");
    if (!userStr) return null;
    const user = JSON.parse(userStr);
    this._cachedUser = user;
    return user;
  },
};